/**
 * VibeLoader - Music equalizer bars loading animation for the web.
 * Pulsing vertical bars at varied tempos for a high-energy "music app" vibe.
 */

import React from "react";

const SIZES = {
  small:   { barW: 3, gap: 3, maxH: 18, minH: 4 },
  default: { barW: 5, gap: 5, maxH: 38, minH: 8 },
  large:   { barW: 7, gap: 6, maxH: 56, minH: 12 },
  xlarge:  { barW: 9, gap: 8, maxH: 80, minH: 16 },
};

// Each bar has a unique animation-duration so they look like a real EQ.
const BAR_TEMPOS = ["380ms", "540ms", "460ms", "620ms", "500ms"];

export const VibeLoader = ({
  size = "default",
  text = "Loading...",
  showText = true,
  color = "#f97316", // orange-500 — matches StoryBox logo gradient start
}) => {
  const cfg = SIZES[size] || SIZES.default;

  return (
    <div className="flex flex-col items-center justify-center" data-testid="vibe-loader">
      <div
        className="flex items-end justify-center"
        style={{ height: cfg.maxH, gap: cfg.gap }}
      >
        {BAR_TEMPOS.map((dur, i) => (
          <span
            key={i}
            className="rounded-full"
            style={{
              width: cfg.barW,
              backgroundColor: color,
              animation: `vibe-eq-bar ${dur} ease-in-out ${i * 80}ms infinite alternate`,
              minHeight: cfg.minH,
              height: cfg.maxH,
              transformOrigin: "bottom center",
            }}
          />
        ))}
      </div>
      {showText && (
        <p
          className="mt-4 font-semibold tracking-wide"
          style={{ fontSize: cfg.barW * 2 + 6, color }}
        >
          {text}
        </p>
      )}
      <style>{`
        @keyframes vibe-eq-bar {
          0%   { transform: scaleY(0.25); }
          100% { transform: scaleY(1); }
        }
      `}</style>
    </div>
  );
};

// Full page loader - centered on background
export const PageLoader = ({ text = "Loading..." }) => (
  <div className="fixed inset-0 flex items-center justify-center bg-black z-50">
    <VibeLoader size="large" text={text} />
  </div>
);

// Inline loader for sections
export const InlineLoader = ({ text = "Loading..." }) => (
  <div className="flex items-center justify-center py-12">
    <VibeLoader size="default" text={text} />
  </div>
);

// Small loader (no text) for buttons/badges
export const SmallLoader = ({ color = "#f97316" }) => (
  <VibeLoader size="small" showText={false} color={color} />
);

export default VibeLoader;
